import { logger } from '@/lib/logger';

import type {
  AnalyticsIdentifyEvent,
  AnalyticsTrackEvent,
} from '@/lib/contexts/analytics';

import type { UserData } from '@/lib/api/types';
import type { UUID } from '@/types';

export interface FetchStoreAndIdentifyDeps {
  fetchClientDetails: (
    siteId: UUID | undefined,
    tenantId: string,
  ) => Promise<UserData>;
  siteId: UUID | undefined;
  tenantId: string;
  lang: string;
  identify?: (event: AnalyticsIdentifyEvent) => void;
  track?: (event: AnalyticsTrackEvent) => void;
}

export const fetchAndStoreAccountDetails = async (
  newUser: boolean,
  deps: Pick<
    FetchStoreAndIdentifyDeps,
    'fetchClientDetails' | 'siteId' | 'tenantId'
  >,
): Promise<UserData> => {
  const responseAccount = await deps.fetchClientDetails(
    deps.siteId,
    deps.tenantId,
  );

  responseAccount.new = newUser;

  localStorage.setItem('userData', JSON.stringify(responseAccount));
  window.dispatchEvent(new Event('userDataUpdated'));

  return responseAccount;
};

export const fetchStoreAndIdentify = async (
  newUser: boolean,
  registrationMethod: string,
  deps: FetchStoreAndIdentifyDeps,
): Promise<void> => {
  const userData = await fetchAndStoreAccountDetails(newUser, deps).catch(
    err => {
      logger.error(err, { context: 'Failed to fetch account details' });
      return null;
    },
  );

  if (userData) {
    deps.identify?.({
      id: String(userData.id),
      traits: {
        email: userData.email,
        language: userData.language,
        country: userData.country,
        subscription_status: userData.subscription?.status,
      },
    });

    if (newUser) {
      deps.track?.({
        type: 'custom',
        eventName: 'user_registered',
        eventData: {
          customer: { type: 'new' },
          locale: deps.lang,
          registration: { method: registrationMethod },
          tenant: { id: deps.tenantId },
        },
      });
    }
  }

  if (newUser) {
    deps.track?.({
      type: 'custom',
      eventName: 'user_registration',
      eventData: {
        customer: { type: 'new' },
        locale: deps.lang,
        registration: { method: registrationMethod },
        tenant: { id: deps.tenantId },
      },
    });
  }
};
